import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import {
  Plus, Edit, Trash2, AlertCircle, Mail, MessageSquare, Phone, Camera, Video, FileText, User, Ticket as TicketIcon,
  Info, ArrowUp, ArrowDown, Smartphone, ChevronRight, LogIn, Globe, List as ListIcon, LayoutGrid,
  AlertTriangle, Shapes,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { AuthorizedReportersPicker } from '@/components/AuthorizedReportersPicker';
import { useBranding } from '@/contexts/BrandingContext';
import { usePermissions } from '@/hooks/usePermissions';
import { fetchAuthorizedReporters } from '@/data/api';
import { api, departmentEndpoints, externalContactTypeEndpoints, getApiErrorMessage, objectIconEndpoints, objectTypeEndpoints, occurrencePriorityEndpoints, occurrenceTypeEndpoints, userEndpoints } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const INCIDENT_TYPE_ACTION_PERMISSIONS = {
  create: PERMISSIONS.INCIDENT_TYPE_CREATE,
  read: PERMISSIONS.INCIDENT_TYPE_READ,
  update: PERMISSIONS.INCIDENT_TYPE_UPDATE,
  delete: PERMISSIONS.INCIDENT_TYPE_DELETE,
};

const normalizeIconOption = (source = {}, index = 0) => {
  const id = source.id ?? source.object_icon_id ?? source.uuid ?? source.key ?? index + 1;
  const label = source.label ?? source.name ?? source.title ?? String(id);
  return {
    ...source,
    id,
    key: String(id),
    label,
    name: label,
    icon: source.icon ?? source.url ?? source.path ?? source.image ?? source.value ?? '',
  };
};

const normalizeIconOptions = (payload) =>
  collectionPayload(payload, 'icons').map(normalizeIconOption).filter(icon => icon.id);

const normalizeExternalContactType = (source = {}, index = 0) => ({
  ...source,
  id: source.id ?? source.external_contact_type_id ?? source.contact_type_id ?? source.uuid ?? index + 1,
  name: source.name ?? source.label ?? source.title ?? `Tipo ${index + 1}`,
  status: Number(source.status ?? source.active ?? 1),
});

const normalizeExternalContactTypes = (payload) => {
  const contactTypes = collectionPayload(payload, 'contact_types');
  const externalContactTypes = contactTypes.length ? contactTypes : collectionPayload(payload, 'external_contact_types');
  return externalContactTypes
    .map(normalizeExternalContactType)
    .filter(type => type.id && type.name);
};

const ApiIcon = ({ icon, size = 16, className = '' }) => {
  const dimension = typeof size === 'number' ? `${size}px` : size;

  if (icon?.icon && /^(https?:|\/|data:)/.test(String(icon.icon))) {
    return (
      <span
        className={`inline-flex items-center justify-center overflow-hidden ${className}`}
        style={{ width: dimension, height: dimension, maxWidth: '100%', maxHeight: '100%' }}
      >
        <img src={icon.icon} alt="" className="h-full w-full object-contain" />
      </span>
    );
  }
  return <Shapes size={size} className={className || 'text-slate-500'} />;
};

const IconPicker = ({ value, onChange, icons = [], loading = false, testIdPrefix = 'icon' }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-xs font-semibold text-slate-600">Ícone *</Label>
    </div>
    <div className="grid grid-cols-9 gap-1.5 p-2 rounded-md border border-slate-200 bg-slate-50" data-testid={`${testIdPrefix}-grid`}>
      {loading && (
        <p className="col-span-9 text-[11px] text-slate-500 text-center py-2">A carregar icones...</p>
      )}
      {!loading && icons.map(icon => {
        const active = String(value || '') === String(icon.id);
        return (
          <button
            key={icon.id}
            type="button"
            onClick={() => onChange(String(icon.id))}
            title={icon.label}
            data-testid={`${testIdPrefix}-${icon.id}`}
            className={`h-8 w-8 flex items-center justify-center rounded border transition-all ${active ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent hover:border-slate-300 text-slate-500'}`}
          >
            <ApiIcon icon={icon} size={15} />
          </button>
        );
      })}
      {!loading && icons.length === 0 && (
        <p className="col-span-9 text-[11px] text-slate-500 text-center py-2">Sem icones disponiveis.</p>
      )}
    </div>
    {!value && (
      <p className="text-[10px] text-red-600">
        Selecione um ícone para este tipo de ocorrência.
      </p>
    )}
  </div>
);
const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const safeJson = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const arrayFrom = (value) => {
  const parsed = safeJson(value, value);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') return Object.values(parsed);
  if (typeof parsed === 'string') return parsed.split(',').map(item => item.trim()).filter(Boolean);
  return [];
};

const boolFrom = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  return value === true || value === 1 || value === '1' || value === 'true';
};

const numberFrom = (...values) => {
  const value = values.find(item => item !== undefined && item !== null);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DEFAULT_EMAIL_SUBJECT = 'Nova ocorrência: {{$tipo}}';
const DEFAULT_EMAIL_BODY = 'Olá {{$nome}},\n\nFoi reportada uma nova ocorrência:\n\n• Tipo: {{$tipo}}\n• Referência: {{$matricula}}\n• Localização: {{$localizacao}}\n• Reportada por: {{$alertante}}\n\nAceda ao sistema para mais detalhes.';
const DEFAULT_SMS_NOTIFICATION_TEXT = 'Ocorrência: {{$tipo}} em {{$matricula}}. Reportar: {{$link}}';
const DEFAULT_CALL_QUESTION = 'Olá {{$nome}}. Foi reportada uma {{$tipo}} em {{$matricula}}.';
const DEFAULT_CALL_OPTIONS = [
  { key: '1', label: 'Ligar para o alertante', action: 'callback' },
  { key: '2', label: 'Avisar que está a chegar', action: 'notify_arriving' },
  { key: '3', label: 'Reportar (gestor / manutenção)', action: 'report' },
  { key: '4', label: 'Ignorar', action: 'ignore' },
];

const cloneDefaultCallOptions = () => DEFAULT_CALL_OPTIONS.map(option => ({...option}));

const normalizeCallOptions = (value) => {
  const options = arrayFrom(value)
    .map((option, index) => {
      if (typeof option === 'string') {
        return {key: String(index + 1), label: option, action: 'callback'};
      }
      if (!option || typeof option !== 'object') return null;
      return {
        key: String(option.key ?? option.digit ?? option.input ?? index + 1),
        label: option.label ?? option.text ?? option.message ?? '',
        action: option.action ?? option.value ?? 'callback',
      };
    })
    .filter(Boolean);

  return options.length ? options : cloneDefaultCallOptions();
};

const normalizeChannelSettings = (source = {}, settings = {}, fallback = {}) => {
  const channelSettings = safeJson(
    source.channel_settings
      ?? source.channel_config
      ?? source.channels_config
      ?? settings.channel_settings
      ?? settings.channel_config,
    {}
  );
  const emailSettings = safeJson(
    source.email_settings ?? source.email_config ?? channelSettings.email ?? settings.email_settings,
    {}
  );
  const smsSettings = safeJson(
    source.sms_settings ?? source.sms_config ?? channelSettings.sms ?? settings.sms_settings,
    {}
  );
  const callSettings = safeJson(
    source.call_settings ?? source.call_config ?? source.ivr_settings ?? channelSettings.call ?? settings.call_settings,
    {}
  );

  return {
    emailSubject: source.email_subject
      ?? source.emailSubject
      ?? settings.email_subject
      ?? emailSettings.subject
      ?? fallback.emailSubject
      ?? DEFAULT_EMAIL_SUBJECT,
    emailBody: source.email_body
      ?? source.emailBody
      ?? settings.email_body
      ?? emailSettings.body
      ?? fallback.emailBody
      ?? DEFAULT_EMAIL_BODY,
    smsNotificationText: source.sms_notification_text
      ?? source.sms_message
      ?? source.sms_body
      ?? settings.sms_notification_text
      ?? smsSettings.notification_text
      ?? smsSettings.message
      ?? fallback.smsNotificationText
      ?? DEFAULT_SMS_NOTIFICATION_TEXT,
    callQuestion: source.call_question
      ?? source.call_message
      ?? source.tts_message
      ?? settings.call_question
      ?? callSettings.question
      ?? callSettings.message
      ?? fallback.callQuestion
      ?? DEFAULT_CALL_QUESTION,
    callOptions: normalizeCallOptions(
      source.call_options
        ?? source.ivr_options
        ?? settings.call_options
        ?? callSettings.options
        ?? fallback.callOptions
    ),
  };
};

const SYSTEM_CATEGORY_LABELS = {
  todas: 'Todas as categorias',
  sem: 'Sem categoria',
};

const isDepartmentCategory = (categoryKey = '') => String(categoryKey).startsWith('department:');
const departmentCategoryKey = (id) => `department:${id}`;
const departmentIdFromCategoryKey = (categoryKey = '') => (
  isDepartmentCategory(categoryKey) ? String(categoryKey).replace('department:', '') : null
);

const categoryLabelFromKey = (categoryKey, departments = []) => {
  const departmentId = departmentIdFromCategoryKey(categoryKey);
  if (departmentId) {
    return departments.find(department => String(department.id) === departmentId)?.name || 'Sem categoria';
  }
  return SYSTEM_CATEGORY_LABELS[categoryKey] || categoryKey || 'Sem categoria';
};

const categoryKeyFromType = (type = {}, departments = []) => {
  const departmentId = type.department_id ?? type.department?.id ?? type.category_id;
  if (departmentId) return departmentCategoryKey(departmentId);
  const category = type.category ?? type.department_name;
  const department = departments.find(item => item.name === category);
  if (department) return departmentCategoryKey(department.id);
  if (
    type.department_id === null
    || type.department?.id === null
    || type.category_id === null
    || category === SYSTEM_CATEGORY_LABELS.todas
  ) return 'todas';
  return 'sem';
};

const normalizeDepartment = (department = {}, index = 0) => ({
  id: department.id ?? department.department_id ?? department.uuid ?? index + 1,
  name: department.name ?? department.label ?? `Categoria ${index + 1}`,
});

const normalizeDepartments = (payload) =>
  collectionPayload(payload, 'departments').map(normalizeDepartment).filter(department => department.id && department.name);

const normalizeTicketNotifyUser = (user = {}, index = 0) => {
  const firstName = user.first_name ?? '';
  const lastName = user.last_name ?? '';
  const derivedName = `${firstName} ${lastName}`.trim();
  return {
    id: user.id ?? user.user_id ?? user.email ?? index + 1,
    name: user.name || derivedName || user.email || `Utilizador ${index + 1}`,
    emailAddress: user.email ?? '',
    phone: user.phone ?? user.phone_number ?? '',
    enabled: false,
    email: !!user.email,
    sms: false,
  };
};

const normalizeTicketNotifyUsers = (payload) =>
  collectionPayload(payload, 'users')
    .map(normalizeTicketNotifyUser)
    .filter(user => user.id && user.name);

const ticketNotifySelectionKeys = (item) => {
  if (item === undefined || item === null) return [];
  if (typeof item !== 'object') return [String(item)];

  const identityValues = [
    item.user_id,
    item.userId,
    item.id,
    item.emailAddress,
    item.name,
  ];

  if (typeof item.email === 'string') {
    identityValues.push(item.email);
  }

  return identityValues
    .filter(value => value !== undefined && value !== null && value !== '')
    .flatMap(value => {
      const key = String(value);
      return [key, key.toLowerCase()];
    });
};

const applyTicketNotifySelection = (users = [], selected = []) => {
  const selectedMap = new Map();
  arrayFrom(selected).forEach(item => {
    ticketNotifySelectionKeys(item).forEach(key => selectedMap.set(key, item));
  });

  return users.map(user => {
    const selectedItem = ticketNotifySelectionKeys(user)
      .map(key => selectedMap.get(key))
      .find(Boolean);

    if (!selectedItem) {
      return {...user, enabled: false};
    }
    const channels = arrayFrom(
      typeof selectedItem === 'object'
        ? selectedItem.channels ?? selectedItem.contact_methods
        : []
    );
    return {
      ...user,
      enabled: true,
      email: typeof selectedItem === 'object' && selectedItem.email !== undefined
        ? boolFrom(selectedItem.email)
        : (channels.length ? channels.includes('email') : user.email),
      sms: typeof selectedItem === 'object' && selectedItem.sms !== undefined
        ? boolFrom(selectedItem.sms)
        : channels.includes('sms'),
    };
  });
};

const ticketNotifyPayload = (users = []) =>
  users
    .filter(user => user.enabled)
    .map(user => ({
      user_id: user.id,
      email: !!user.email,
      sms: !!user.sms,
      channels: [user.email && 'email', user.sms && 'sms'].filter(Boolean),
    }));

const normalizeObjectType = (objectType = {}, index = 0) => {
  const id = objectType.id
    ?? objectType.object_type_id
    ?? objectType.type_id
    ?? objectType.uuid
    ?? index + 1;
  const color = objectType.color
    ?? objectType.hex_color
    ?? objectType.object_color?.color
    ?? objectType.object_color?.hex_color
    ?? '#64748b';

  return {
    ...objectType,
    id,
    key: String(id),
    name: objectType.name ?? objectType.label ?? objectType.title ?? `Tipo de objeto ${index + 1}`,
    color,
    icon: objectType.icon
      ?? objectType.icon_name
      ?? objectType.object_icon?.icon
      ?? null,
  };
};

const normalizeObjectTypes = (payload) => {
  const objectTypes = collectionPayload(payload, 'object_types');
  const types = objectTypes.length ? objectTypes : collectionPayload(payload, 'types');
  return types.map(normalizeObjectType).filter(objectType => objectType.id && objectType.name);
};

const findObjectTypeById = (objectTypes = [], id) =>
  objectTypes.find(objectType => String(objectType.id) === String(id));

const countSelectedObjectTypes = (objectTypeIds = [], objectTypes = []) => {
  if (!objectTypeIds || objectTypeIds.length === 0) return objectTypes.length;
  const uniqueIds = [...new Set(objectTypeIds.map(id => String(id)))];
  return uniqueIds.filter(id => findObjectTypeById(objectTypes, id)).length || uniqueIds.length;
};

const normalizeOccurrencePriority = (priority = {}, index = 0) => {
  const id = priority.id ?? priority.occurrence_priority_id ?? priority.uuid ?? index + 1;
  return {
    ...priority,
    id,
    key: String(id),
    priority: numberFrom(priority.priority, index),
    name: priority.name ?? priority.label ?? `Prioridade ${index + 1}`,
    description: priority.description ?? '',
    object_color_id: priority.object_color_id ?? null,
    object_icon_id: priority.object_icon_id ?? null,
  };
};

const normalizeOccurrencePriorities = (payload) => {
  const priorities = collectionPayload(payload, 'priorities');
  const occurrencePriorities = priorities.length ? priorities : collectionPayload(payload, 'occurrence_priorities');
  return occurrencePriorities
    .map(normalizeOccurrencePriority)
    .filter(priority => priority.id)
    .sort((a, b) => Number(a.priority) - Number(b.priority) || String(a.name).localeCompare(String(b.name)));
};

const priorityKeyFromType = (type = {}, priorities = []) => {
  const priorityId = type.occurrence_priority_id ?? type.priority_id ?? type.occurrence_priority?.id;
  if (priorityId) return String(priorityId);
  const rawPriority = type.priority;
  const matched = priorities.find(item =>
    String(item.id) === String(rawPriority)
    || String(item.priority) === String(rawPriority)
    || item.name === rawPriority
  );
  return matched ? String(matched.id) : String(rawPriority || '');
};

const priorityLabelFromKey = (priorityKey, priorities = []) => {
  const priority = priorities.find(item => String(item.id) === String(priorityKey));
  return priority?.name || priorityKey || '';
};

const priorityNumberFromKey = (priorityKey, priorities = []) => {
  const priority = priorities.find(item => String(item.id) === String(priorityKey));
  return priority?.priority ?? numberFrom(priorityKey);
};

const normalizeIncidentType = (source = {}, fallback = {}, departments = [], priorities = []) => {
  const settings = safeJson(source.settings ?? source.config ?? source.options, {});
  const channelSettings = normalizeChannelSettings(source, settings, fallback);
  const contactMethods = arrayFrom(source.contact_methods ?? source.channels ?? settings.contact_methods);
  const objectTypeIds = arrayFrom(source.object_type_ids ?? source.object_types ?? settings.object_type_ids ?? fallback.objectTypeIds)
    .map(item => typeof item === 'object' ? item.id ?? item.object_type_id : item)
    .filter(item => item !== undefined && item !== null);
  const priority = priorityKeyFromType(source, priorities)
    || source.priority
    || source.priority_code
    || source.occurrence_priority?.code
    || settings.priority
    || fallback.priority
    || '';
  const phoneMode = source.phone_mode ?? settings.phone_mode ?? fallback.phoneMode ?? '';
  const requirePhone = source.require_phone !== undefined || settings.require_phone !== undefined
    ? boolFrom(source.require_phone ?? settings.require_phone)
    : (phoneMode ? phoneMode !== 'any' : fallback.requirePhone ?? false);
  const confirmPhone = source.confirm_phone !== undefined || settings.confirm_phone !== undefined
    ? boolFrom(source.confirm_phone ?? settings.confirm_phone)
    : (phoneMode ? phoneMode === 'confirm' : fallback.confirmPhone ?? false);
  const departmentId = source.department_id ?? source.department?.id ?? fallback.department_id ?? null;
  const availableForAll = source.available_for_all !== undefined || settings.available_for_all !== undefined
    ? boolFrom(source.available_for_all ?? settings.available_for_all)
    : objectTypeIds.length === 0;

  return {
    ...fallback,
    ...source,
    id: source.id ?? source.occurrence_type_id ?? fallback.id,
    uuid: source.uuid ?? fallback.uuid,
    department_id: departmentId,
    name: source.name ?? fallback.name ?? '',
    description: source.description ?? fallback.description ?? '',
    category: source.category
      ?? source.department?.name
      ?? source.department_name
      ?? departments.find(department => String(department.id) === String(departmentId))?.name
      ?? settings.category
      ?? fallback.category
      ?? (departmentId === null ? SYSTEM_CATEGORY_LABELS.todas : 'Sem categoria'),
    objectTypeIds,
    availableForAll,
    priority: String(priority),
    contactMethods: contactMethods.length ? contactMethods : (fallback.contactMethods || []),
    requirePhone,
    confirmPhone,
    phoneMode: phoneMode || (confirmPhone ? 'confirm' : (requirePhone ? 'registered_only' : 'any')),
    icon: source.object_icon_id ?? source.icon_id ?? source.icon ?? source.icon_name ?? settings.icon ?? fallback.icon ?? null,
    authorizedReporterIds: arrayFrom(source.authorized_reporter_ids ?? settings.authorized_reporter_ids ?? fallback.authorizedReporterIds),
    generateTicket: boolFrom(source.generate_ticket ?? settings.generate_ticket, fallback.generateTicket ?? false),
    notifyUsers: arrayFrom(source.notify_users ?? settings.notify_users ?? fallback.notifyUsers),
    emailSubject: channelSettings.emailSubject,
    emailBody: channelSettings.emailBody,
    smsNotificationText: channelSettings.smsNotificationText,
    callQuestion: channelSettings.callQuestion,
    callOptions: channelSettings.callOptions,
    allowPhotos: boolFrom(source.allow_photos ?? settings.allow_photos, fallback.allowPhotos ?? true),
    allowVideos: boolFrom(source.allow_videos ?? settings.allow_videos, fallback.allowVideos ?? false),
    allowText: boolFrom(source.allow_text ?? settings.allow_text, fallback.allowText ?? true),
    active: boolFrom(source.active ?? source.status ?? settings.active, fallback.active ?? true),
    totalReports: numberFrom(source.total_reports, source.reports_count, fallback.totalReports),
  };
};

const normalizeIncidentTypes = (payload, departments = [], priorities = []) => {
  const occurrenceTypes = collectionPayload(payload, 'occurrence_types');
  const types = occurrenceTypes.length ? occurrenceTypes : collectionPayload(payload, 'types');
  return types.map((type, index) => normalizeIncidentType(type, {id: index + 1}, departments, priorities));
};

const incidentTypePayload = ({
  name,
  description,
  categoryKey,
  priority,
  contactMethods,
  phoneMode,
  icon,
  authorizedReporterIds,
  generateTicket,
  notifyUsers = [],
  emailSubject = DEFAULT_EMAIL_SUBJECT,
  emailBody = DEFAULT_EMAIL_BODY,
  smsNotificationText = DEFAULT_SMS_NOTIFICATION_TEXT,
  callQuestion = DEFAULT_CALL_QUESTION,
  callOptions = cloneDefaultCallOptions(),
  allowPhotos,
  allowVideos,
  allowText,
  active,
  objectTypeIds = [],
  availableForAll = false,
  priorities = [],
}) => {
  const selectedPriority = priorities.find(item => String(item.id) === String(priority));
  const normalizedCallOptions = normalizeCallOptions(callOptions);
  const channelSettings = {
    email: {
      subject: String(emailSubject || '').trim(),
      body: String(emailBody || '').trim(),
    },
    sms: {
      message: String(smsNotificationText || '').trim(),
    },
    call: {
      question: String(callQuestion || '').trim(),
      options: normalizedCallOptions,
    },
  };
  return {
    name: name.trim(),
    description: description.trim() || null,
    department_id: departmentIdFromCategoryKey(categoryKey),
    occurrence_priority_id: selectedPriority?.id ?? null,
    contact_methods: contactMethods,
    object_type_ids: availableForAll ? [] : objectTypeIds,
    phone_mode: phoneMode,
    object_icon_id: icon ? Number(icon) : null,
    authorized_reporter_ids: authorizedReporterIds,
    generate_ticket: generateTicket,
    notify_users: notifyUsers,
    channel_settings: channelSettings,
    allow_photos: allowPhotos,
    allow_videos: allowVideos,
    allow_text: allowText,
    status: active ? 1 : 0,
  };
};

export const IncidentTypesConfig = () => {
  const { branding } = useBranding();
  const { can } = usePermissions();
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [icons, setIcons] = useState([]);
  const [objectTypes, setObjectTypes] = useState([]);
  const [authorizedReporters, setAuthorizedReporters] = useState([]);
  const [externalContactTypes, setExternalContactTypes] = useState([]);
  const [ticketNotifyUsers, setTicketNotifyUsers] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [prioritiesLoading, setPrioritiesLoading] = useState(true);
  const [iconsLoading, setIconsLoading] = useState(true);
  const [objectTypesLoading, setObjectTypesLoading] = useState(true);
  const [authorizedReportersLoading, setAuthorizedReportersLoading] = useState(true);
  const [externalContactTypesLoading, setExternalContactTypesLoading] = useState(true);
  const [ticketNotifyUsersLoading, setTicketNotifyUsersLoading] = useState(true);
  const [typesError, setTypesError] = useState('');
  const [departmentsError, setDepartmentsError] = useState('');
  const [prioritiesError, setPrioritiesError] = useState('');
  const [iconsError, setIconsError] = useState('');
  const [objectTypesError, setObjectTypesError] = useState('');
  const [authorizedReportersError, setAuthorizedReportersError] = useState('');
  const [externalContactTypesError, setExternalContactTypesError] = useState('');
  const [ticketNotifyUsersError, setTicketNotifyUsersError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  // Quick-create form (mini)
  const [qcName, setQcName] = useState('');
  const [qcDescription, setQcDescription] = useState('');
  const [qcCategory, setQcCategory] = useState('todas');
  const [qcObjectTypes, setQcObjectTypes] = useState([]); // ids dos tipos de objeto
  const [qcAvailableForAll, setQcAvailableForAll] = useState(false); // disponível para todos os tipos de objeto
  const [qcIcon, setQcIcon] = useState(null);
  const [editingTypeId, setEditingTypeId] = useState(null);

  // Form states
  const [typeName, setTypeName] = useState('');
  const [typeDescription, setTypeDescription] = useState('');
  const [typeCategory, setTypeCategory] = useState('todas');
  const [typePriority, setTypePriority] = useState('');
  const [contactEmail, setContactEmail] = useState(true);
  const [contactSMS, setContactSMS] = useState(false);
  const [contactCall, setContactCall] = useState(false);

  // Editor states for each channel
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);

  // SMS único de envio (notificação ao alertante)
  const [smsNotificationText, setSmsNotificationText] = useState(DEFAULT_SMS_NOTIFICATION_TEXT);

  const [callQuestion, setCallQuestion] = useState(DEFAULT_CALL_QUESTION);
  const [callOptions, setCallOptions] = useState(cloneDefaultCallOptions);
  const [requirePhone, setRequirePhone] = useState(false);
  // Modo de contacto telefónico do alertante:
  //   'any'              → qualquer número (sem confirmação)
  //   'confirm'          → obriga SMS OTP de confirmação
  //   'registered_only'  → rejeita números não registados no sistema
  const [phoneMode, setPhoneMode] = useState('any');
  const [typeIcon, setTypeIcon] = useState(null);
  const [typeObjectTypes, setTypeObjectTypes] = useState([]);
  const [typeAvailableForAll, setTypeAvailableForAll] = useState(true);
  const [previewMobileOpen, setPreviewMobileOpen] = useState(false);
  const [previewMobileLayout, setPreviewMobileLayout] = useState('list'); // 'list' | 'grid'

  // Pool global de Alertantes Autorizados. Estes contactos não fazem login.
  // IDs do pool selecionados para o tipo de ocorrência atualmente em edição
  const [typeAuthorizedReporterIds, setTypeAuthorizedReporterIds] = useState([]);

  // Reordenação de tipos
  const moveType = (idx, dir) => {
    setIncidentTypes(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const [confirmPhone, setConfirmPhone] = useState(false);
  const [generateTicket, setGenerateTicket] = useState(true);
  const [allowPhotos, setAllowPhotos] = useState(true);
  const [allowVideos, setAllowVideos] = useState(false);
  const [allowText, setAllowText] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const incidentTypePermissions = {
    create: can(INCIDENT_TYPE_ACTION_PERMISSIONS.create),
    read: can(INCIDENT_TYPE_ACTION_PERMISSIONS.read),
    update: can(INCIDENT_TYPE_ACTION_PERMISSIONS.update),
    delete: can(INCIDENT_TYPE_ACTION_PERMISSIONS.delete),
  };
  const canReadIncidentTypes = incidentTypePermissions.read;
  const canCreateIncidentTypes = incidentTypePermissions.create;
  const canUpdateIncidentTypes = incidentTypePermissions.update;
  const canDeleteIncidentTypes = incidentTypePermissions.delete;

  useEffect(() => {
    let cancelled = false;

    const loadIncidentTypes = async () => {
      if (!canReadIncidentTypes) {
        setIncidentTypes([]);
        setDepartments([]);
        setPriorities([]);
        setIcons([]);
        setObjectTypes([]);
        setAuthorizedReporters([]);
        setExternalContactTypes([]);
        setTicketNotifyUsers([]);
        setTypesError('Nao tem permissão para consultar tipos de ocorrência.');
        setDepartmentsError('');
        setPrioritiesError('');
        setIconsError('');
        setObjectTypesError('');
        setAuthorizedReportersError('');
        setExternalContactTypesError('');
        setTicketNotifyUsersError('');
        setLoadingTypes(false);
        setDepartmentsLoading(false);
        setPrioritiesLoading(false);
        setIconsLoading(false);
        setObjectTypesLoading(false);
        setAuthorizedReportersLoading(false);
        setExternalContactTypesLoading(false);
        setTicketNotifyUsersLoading(false);
        return;
      }

      setLoadingTypes(true);
      setDepartmentsLoading(true);
      setPrioritiesLoading(true);
      setIconsLoading(true);
      setObjectTypesLoading(true);
      setAuthorizedReportersLoading(true);
      setExternalContactTypesLoading(true);
      setTicketNotifyUsersLoading(true);
      setTypesError('');
      setDepartmentsError('');
      setPrioritiesError('');
      setIconsError('');
      setObjectTypesError('');
      setAuthorizedReportersError('');
      setExternalContactTypesError('');
      setTicketNotifyUsersError('');

      try {
        const [typesResponse, departmentsResponse, prioritiesResponse, iconsResponse, objectTypesResponse, authorizedReportersResponse, contactTypesResponse, usersResponse] = await Promise.all([
          api.get(occurrenceTypeEndpoints.listCompact),
          api.get(departmentEndpoints.list).catch(error => ({data: [], error})),
          api.get(occurrencePriorityEndpoints.list).catch(error => ({data: [], error})),
          api.get(objectIconEndpoints.list).catch(error => ({data: [], error})),
          api.get(objectTypeEndpoints.list).catch(error => ({data: [], error})),
          fetchAuthorizedReporters().then(data => ({data})).catch(error => ({data: [], error})),
          api.get(externalContactTypeEndpoints.list).catch(error => ({data: [], error})),
          api.get(userEndpoints.list).catch(error => ({data: [], error})),
        ]);
        if (!cancelled) {
          const nextDepartments = normalizeDepartments(departmentsResponse.data);
          const nextPriorities = normalizeOccurrencePriorities(prioritiesResponse.data);
          const nextIcons = normalizeIconOptions(iconsResponse.data);
          const nextObjectTypes = normalizeObjectTypes(objectTypesResponse.data);
          const nextAuthorizedReporters = authorizedReportersResponse.data;
          const nextExternalContactTypes = normalizeExternalContactTypes(contactTypesResponse.data);
          const nextTicketNotifyUsers = normalizeTicketNotifyUsers(usersResponse.data);
          setDepartments(nextDepartments);
          setPriorities(nextPriorities);
          setIcons(nextIcons);
          setObjectTypes(nextObjectTypes);
          setAuthorizedReporters(nextAuthorizedReporters);
          setExternalContactTypes(nextExternalContactTypes);
          setTicketNotifyUsers(nextTicketNotifyUsers);
          if (departmentsResponse.error) {
            setDepartmentsError(getApiErrorMessage(departmentsResponse.error, 'Nao foi possível carregar as categorias.'));
          }
          if (prioritiesResponse.error) {
            setPrioritiesError(getApiErrorMessage(prioritiesResponse.error, 'Nao foi possível carregar os níveis de prioridade.'));
          }
          if (iconsResponse.error) {
            setIconsError(getApiErrorMessage(iconsResponse.error, 'Nao foi possível carregar os icones.'));
          }
          if (objectTypesResponse.error) {
            setObjectTypesError(getApiErrorMessage(objectTypesResponse.error, 'Nao foi possível carregar os tipos de objeto.'));
          }
          if (authorizedReportersResponse.error) {
            setAuthorizedReportersError(getApiErrorMessage(authorizedReportersResponse.error, 'Nao foi possível carregar os contactos.'));
          }
          if (contactTypesResponse.error) {
            setExternalContactTypesError(getApiErrorMessage(contactTypesResponse.error, 'Nao foi possivel carregar os tipos de contacto.'));
          }
          if (usersResponse.error) {
            setTicketNotifyUsersError(getApiErrorMessage(usersResponse.error, 'Nao foi possível carregar os utilizadores.'));
          }
          setIncidentTypes(normalizeIncidentTypes(typesResponse.data, nextDepartments, nextPriorities));
        }
      } catch (error) {
        if (!cancelled) {
          setIncidentTypes([]);
          setTypesError(getApiErrorMessage(error, 'Nao foi possível carregar os tipos de ocorrência.'));
        }
      } finally {
        if (!cancelled) {
          setLoadingTypes(false);
          setDepartmentsLoading(false);
          setPrioritiesLoading(false);
          setIconsLoading(false);
          setObjectTypesLoading(false);
          setAuthorizedReportersLoading(false);
          setExternalContactTypesLoading(false);
          setTicketNotifyUsersLoading(false);
        }
      }
    };

    loadIncidentTypes();

    return () => {
      cancelled = true;
    };
  }, [canReadIncidentTypes]);

  // Reset form to defaults
  const resetForm = () => {
    setTypeName('');
    setTypeDescription('');
    setTypeCategory('todas');
    setTypePriority(priorities[0] ? String(priorities[0].id) : '');
    setContactEmail(true);
    setContactSMS(false);
    setContactCall(false);
    setEmailSubject(DEFAULT_EMAIL_SUBJECT);
    setEmailBody(DEFAULT_EMAIL_BODY);
    setSmsNotificationText(DEFAULT_SMS_NOTIFICATION_TEXT);
    setCallQuestion(DEFAULT_CALL_QUESTION);
    setCallOptions(cloneDefaultCallOptions());
    setRequirePhone(false);
    setConfirmPhone(false);
    setPhoneMode('any');
    setTypeIcon(null);
    setTypeObjectTypes([]);
    setTypeAvailableForAll(true);
    setTypeAuthorizedReporterIds([]);
    setGenerateTicket(true);
    setTicketNotifyUsers(prev => applyTicketNotifySelection(prev, []));
    setAllowPhotos(true);
    setAllowVideos(false);
    setAllowText(true);
    setIsActive(true);
    setEditingTypeId(null);
    setIsEditMode(false);
  };

  // Open dialog for creating new type
  const handleOpenCreate = () => {
    if (!canCreateIncidentTypes) {
      toast.error('Nao tem permissão para criar tipos de ocorrência.');
      return;
    }
    resetForm();
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  // Open quick-create mini dialog (apenas o essencial)
  const handleOpenQuickCreate = () => {
    if (!canCreateIncidentTypes) {
      toast.error('Nao tem permissão para criar tipos de ocorrência.');
      return;
    }
    setQcName('');
    setQcDescription('');
    setQcCategory('todas');
    setQcObjectTypes([]);
    setQcAvailableForAll(false);
    setQcIcon(null);
    setIsQuickCreateOpen(true);
  };

  const handleQuickCreate = async ({ thenEdit = false } = {}) => {
    if (!canCreateIncidentTypes) {
      toast.error('Nao tem permissão para criar tipos de ocorrência.');
      return;
    }
    if (thenEdit && !canUpdateIncidentTypes) {
      toast.error('Nao tem permissão para editar tipos de ocorrência.');
      return;
    }
    if (!qcName.trim()) { toast.error('Nome é obrigatório.'); return; }
    if (!qcIcon) { toast.error('Ícone é obrigatório.'); return; }
    if (!qcAvailableForAll && qcObjectTypes.length === 0) {
      toast.error('Selecione pelo menos um tipo de objeto ou marque "Disponível para todos".');
      return;
    }

    setSubmitting(true);
    try {
      const fallback = {
        id: Date.now(),
        name: qcName.trim(),
        description: qcDescription.trim() || '-',
        category: categoryLabelFromKey(qcCategory, departments),
        objectTypeIds: qcAvailableForAll ? [] : [...qcObjectTypes],
        availableForAll: qcAvailableForAll,
        icon: qcIcon || null,
        priority: priorities[0] ? String(priorities[0].id) : '',
        contactMethods: ['email'],
        requirePhone: false,
        confirmPhone: false,
        phoneMode: 'any',
        generateTicket: true,
        emailSubject: DEFAULT_EMAIL_SUBJECT,
        emailBody: DEFAULT_EMAIL_BODY,
        smsNotificationText: DEFAULT_SMS_NOTIFICATION_TEXT,
        callQuestion: DEFAULT_CALL_QUESTION,
        callOptions: cloneDefaultCallOptions(),
        allowPhotos: true,
        allowVideos: false,
        allowText: true,
        active: true,
        totalReports: 0,
      };
      const response = await api.post(occurrenceTypeEndpoints.create, incidentTypePayload({
        name: qcName,
        description: qcDescription,
        categoryKey: qcCategory,
        priority: priorities[0] ? String(priorities[0].id) : '',
        contactMethods: ['email'],
        requirePhone: false,
        confirmPhone: false,
        phoneMode: 'any',
        icon: qcIcon || null,
        authorizedReporterIds: [],
        generateTicket: false,
        emailSubject: DEFAULT_EMAIL_SUBJECT,
        emailBody: DEFAULT_EMAIL_BODY,
        smsNotificationText: DEFAULT_SMS_NOTIFICATION_TEXT,
        callQuestion: DEFAULT_CALL_QUESTION,
        callOptions: cloneDefaultCallOptions(),
        allowPhotos: true,
        allowVideos: false,
        allowText: true,
        active: true,
        objectTypeIds: qcObjectTypes,
        availableForAll: qcAvailableForAll,
        departments,
        priorities,
      }));
      const created = normalizeIncidentType(response.data?.data ?? response.data?.type ?? response.data?.occurrence_type ?? response.data, fallback, departments, priorities);
      setIncidentTypes(prev => [...prev, created]);
      setIsQuickCreateOpen(false);
      toast.success(`Tipo "${created.name}" criado.${thenEdit ? ' Abrindo configuração avançada...' : ''}`);
      if (thenEdit) {
        setTimeout(() => handleOpenEdit(created), 250);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível criar o tipo de ocorrencia.'));
    } finally {
      setSubmitting(false);
    }
  };
  // Open dialog for editing existing type
  const handleOpenEdit = async (type) => {
    if (!canUpdateIncidentTypes) {
      toast.error('Nao tem permissão para editar tipos de ocorrência.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.get(occurrenceTypeEndpoints.detail(type.id));
      type = normalizeIncidentType(
        response.data?.data ?? response.data?.type ?? response.data?.occurrence_type ?? response.data,
        type,
        departments,
        priorities
      );
      setIncidentTypes(prev => prev.map(item => String(item.id) === String(type.id) ? type : item));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel carregar o tipo de ocorrencia.'));
      return;
    } finally {
      setSubmitting(false);
    }

    setTypeName(type.name);
    setTypeDescription(type.description);
    setTypeCategory(categoryKeyFromType(type, departments));
    setTypePriority(priorityKeyFromType(type, priorities));
    setContactEmail(type.contactMethods.includes('email'));
    setContactSMS(type.contactMethods.includes('sms'));
    setContactCall(type.contactMethods.includes('call'));
    setEmailSubject(type.emailSubject || DEFAULT_EMAIL_SUBJECT);
    setEmailBody(type.emailBody || DEFAULT_EMAIL_BODY);
    setSmsNotificationText(type.smsNotificationText || DEFAULT_SMS_NOTIFICATION_TEXT);
    setCallQuestion(type.callQuestion || DEFAULT_CALL_QUESTION);
    setCallOptions(normalizeCallOptions(type.callOptions));
    setRequirePhone(type.requirePhone);
    setConfirmPhone(type.confirmPhone);
    // Derivar phoneMode a partir dos booleans existentes.
    setPhoneMode(
      type.phoneMode
        ? type.phoneMode
        : (type.confirmPhone ? 'confirm' : (type.requirePhone ? 'registered_only' : 'any'))
    );
    setTypeIcon(type.icon || null);
    setTypeObjectTypes(type.objectTypeIds || []);
    setTypeAvailableForAll(type.availableForAll ?? ((type.objectTypeIds || []).length === 0));
    setTypeAuthorizedReporterIds(type.authorizedReporterIds || []);
    setGenerateTicket(type.generateTicket);
    setTicketNotifyUsers(prev => applyTicketNotifySelection(prev, type.notifyUsers || []));
    setAllowPhotos(type.allowPhotos);
    setAllowVideos(type.allowVideos);
    setAllowText(type.allowText);
    setIsActive(type.active);
    setEditingTypeId(type.id);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  // Save type (create or update)
  const handleSave = async () => {
    if (isEditMode && !canUpdateIncidentTypes) {
      toast.error('Nao tem permissão para editar tipos de ocorrencia.');
      return;
    }
    if (!isEditMode && !canCreateIncidentTypes) {
      toast.error('Nao tem permissão para criar tipos de ocorrência.');
      return;
    }

    const contactMethods = [];
    if (contactEmail) contactMethods.push('email');
    if (contactSMS) contactMethods.push('sms');
    if (contactCall) contactMethods.push('call');

    if (!typeName.trim()) { toast.error('Nome é obrigatório.'); return; }
    if (!typeCategory) { toast.error('Categoria é obrigatória.'); return; }
    if (!typePriority) { toast.error('Prioridade é obrigatória.'); return; }
    if (!typeIcon) { toast.error('Ícone é obrigatório.'); return; }
    if (!typeAvailableForAll && typeObjectTypes.length === 0) {
      toast.error('Selecione pelo menos um tipo de objeto ou marque "Disponível para todos".');
      return;
    }

    const effRequirePhone = phoneMode !== 'any';
    const effConfirmPhone = phoneMode === 'confirm';
    const payload = incidentTypePayload({
      name: typeName,
      description: typeDescription,
      categoryKey: typeCategory,
      priority: typePriority,
      contactMethods,
      requirePhone: effRequirePhone,
      confirmPhone: effConfirmPhone,
      phoneMode,
      icon: typeIcon || null,
      authorizedReporterIds: typeAuthorizedReporterIds,
      generateTicket,
      notifyUsers: ticketNotifyPayload(ticketNotifyUsers),
      emailSubject,
      emailBody,
      smsNotificationText,
      callQuestion,
      callOptions,
      allowPhotos,
      allowVideos,
      allowText,
      active: isActive,
      objectTypeIds: typeObjectTypes,
      availableForAll: typeAvailableForAll,
      departments,
      priorities,
    });

    setSubmitting(true);
    try {
      if (isEditMode && editingTypeId) {
        const current = incidentTypes.find(t => t.id === editingTypeId);
        const response = await api.post(occurrenceTypeEndpoints.update, {
          id: editingTypeId,
          uuid: current?.uuid,
          ...payload,
        });
        const updated = normalizeIncidentType(response.data?.data ?? response.data?.type ?? response.data?.occurrence_type ?? response.data, {
          ...current,
          ...payload,
          category: categoryLabelFromKey(typeCategory, departments),
          contactMethods,
          requirePhone: effRequirePhone,
          confirmPhone: effConfirmPhone,
          phoneMode,
          icon: typeIcon || null,
          objectTypeIds: typeAvailableForAll ? [] : [...typeObjectTypes],
          availableForAll: typeAvailableForAll,
          authorizedReporterIds: [...typeAuthorizedReporterIds],
          generateTicket,
          notifyUsers: ticketNotifyPayload(ticketNotifyUsers),
          emailSubject,
          emailBody,
          smsNotificationText,
          callQuestion,
          callOptions: normalizeCallOptions(callOptions),
          allowPhotos,
          allowVideos,
          allowText,
          active: isActive,
        }, departments, priorities);
        setIncidentTypes(prev => prev.map(t => t.id === editingTypeId ? updated : t));
        toast.success('Tipo de ocorrencia atualizado com sucesso!');
      } else {
        const response = await api.post(occurrenceTypeEndpoints.create, payload);
        const created = normalizeIncidentType(response.data?.data ?? response.data?.type ?? response.data?.occurrence_type ?? response.data, {
          id: Date.now(),
          ...payload,
          category: categoryLabelFromKey(typeCategory, departments),
          contactMethods,
          requirePhone: effRequirePhone,
          confirmPhone: effConfirmPhone,
          phoneMode,
          icon: typeIcon || null,
          objectTypeIds: typeAvailableForAll ? [] : [...typeObjectTypes],
          availableForAll: typeAvailableForAll,
          authorizedReporterIds: [...typeAuthorizedReporterIds],
          generateTicket,
          notifyUsers: ticketNotifyPayload(ticketNotifyUsers),
          emailSubject,
          emailBody,
          smsNotificationText,
          callQuestion,
          callOptions: normalizeCallOptions(callOptions),
          allowPhotos,
          allowVideos,
          allowText,
          active: isActive,
          totalReports: 0,
        }, departments, priorities);
        setIncidentTypes(prev => [...prev, created]);
        toast.success('Tipo de ocorrencia criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel guardar o tipo de ocorrencia.'));
    } finally {
      setSubmitting(false);
    }
  };
  // Delete type
  const handleDelete = async (typeId) => {
    if (!canDeleteIncidentTypes) {
      toast.error('Nao tem permissao para eliminar tipos de ocorrencia.');
      return;
    }
    const type = incidentTypes.find(t => t.id === typeId);
    if (!type) return;
    if (!window.confirm(`Eliminar tipo "${type.name}"?`)) return;

    setSubmitting(true);
    try {
      await api.post(occurrenceTypeEndpoints.delete, {
        id: type.id,
        uuid: type.uuid,
      });
      setIncidentTypes(prev => prev.filter(t => t.id !== typeId));
      toast.success('Tipo de ocorrencia removido!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível remover o tipo de ocorrência.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (typeId) => {
    if (!canUpdateIncidentTypes) {
      toast.error('Nao tem permissao para editar tipos de ocorrencia.');
      return;
    }
    const type = incidentTypes.find(t => t.id === typeId);
    if (!type) return;

    const nextActive = !type.active;
    setSubmitting(true);
    try {
      const payload = incidentTypePayload({
        name: type.name,
        description: type.description || '',
        categoryKey: categoryKeyFromType(type, departments),
        priority: type.priority,
        contactMethods: type.contactMethods || [],
        requirePhone: type.requirePhone,
        confirmPhone: type.confirmPhone,
        phoneMode: type.phoneMode || 'any',
        icon: type.icon || null,
        authorizedReporterIds: type.authorizedReporterIds || [],
        generateTicket: type.generateTicket,
        notifyUsers: type.notifyUsers || [],
        emailSubject: type.emailSubject || DEFAULT_EMAIL_SUBJECT,
        emailBody: type.emailBody || DEFAULT_EMAIL_BODY,
        smsNotificationText: type.smsNotificationText || DEFAULT_SMS_NOTIFICATION_TEXT,
        callQuestion: type.callQuestion || DEFAULT_CALL_QUESTION,
        callOptions: normalizeCallOptions(type.callOptions),
        allowPhotos: type.allowPhotos,
        allowVideos: type.allowVideos,
        allowText: type.allowText,
        active: nextActive,
        objectTypeIds: type.objectTypeIds || [],
        availableForAll: type.availableForAll ?? false,
        departments,
        priorities,
      });
      const response = await api.post(occurrenceTypeEndpoints.update, {
        id: type.id,
        uuid: type.uuid,
        ...payload,
      });
      const updated = normalizeIncidentType(response.data?.data ?? response.data?.type ?? response.data?.occurrence_type ?? response.data, {
        ...type,
        active: nextActive,
      }, departments, priorities);
      setIncidentTypes(prev => prev.map(t => t.id === typeId ? updated : t));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel alterar o estado do tipo de ocorrencia.'));
    } finally {
      setSubmitting(false);
    }
  };
  const getContactMethodsDisplay = (methods) => {
    const icons = [];
    if (methods.includes('email')) icons.push(<Mail key="email" size={14} className="text-blue-600" />);
    if (methods.includes('sms')) icons.push(<MessageSquare key="sms" size={14} className="text-emerald-600" />);
    if (methods.includes('call')) icons.push(<Phone key="call" size={14} className="text-red-600" />);
    return icons;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Configuração de Tipos de Ocorrência</h1>
            <p className="text-slate-600 font-primary mt-1">Definir tipos de ocorrência e comportamento do sistema</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewMobileOpen(true)} data-testid="preview-mobile-btn">
              <Smartphone size={18} className="mr-2" />
              Pré-visualizar (mobile)
            </Button>
          {canCreateIncidentTypes && (
            <Button data-testid="create-incident-type-btn" className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenQuickCreate} disabled={loadingTypes || departmentsLoading || prioritiesLoading || objectTypesLoading || submitting}>
              <Plus size={18} className="mr-2" />
              Novo Tipo
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditMode ? 'Editar Tipo de Ocorrência' : 'Configuração Avançada'}</DialogTitle>
                <DialogDescription>
                  {isEditMode
                    ? 'Configure todos os canais (Email/SMS/Chamada), validações, anexos e ticket.'
                    : 'Configuração completa — canais, validações, anexos e geração de ticket.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h3 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                    <AlertCircle size={18} />
                    Informações Básicas
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="type-name">Nome do Tipo *</Label>
                      <Input id="type-name" placeholder="Ex: Carregador Avariado" value={typeName} onChange={(e) => setTypeName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type-category">Filial/Categoria *</Label>
                      <Select value={typeCategory} onValueChange={setTypeCategory}>
                        <SelectTrigger id="type-category">
                          <SelectValue placeholder="Selecionar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todas" key={0}>Todas as categorias</SelectItem>
                          {departments.map(department => (
                            <SelectItem key={department.id} value={departmentCategoryKey(department.id)}>
                              {department.name}
                            </SelectItem>
                          ))}
                          {/*<SelectItem value="sem">Sem categoria</SelectItem>*/}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type-description">Descrição</Label>
                    <Textarea
                      id="type-description"
                      placeholder="Descreva quando este tipo deve ser usado..."
                      rows={2}
                      value={typeDescription}
                      onChange={(e) => setTypeDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type-priority">Prioridade</Label>
                    <Select value={typePriority} onValueChange={setTypePriority}>
                      <SelectTrigger id="type-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map(priority => (
                          <SelectItem key={priority.id} value={String(priority.id)}>
                            {priority.name}
                          </SelectItem>
                        ))}</SelectContent>
                    </Select>
                  </div>

                  {/* Ícone */}
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <IconPicker value={typeIcon} onChange={setTypeIcon} icons={icons} loading={iconsLoading} testIdPrefix="type-icon" />
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold">Tipos de Objeto aplicaveis *</Label>
                      <span className="text-[10px] text-slate-500">
                        {typeAvailableForAll ? 'todos' : `${typeObjectTypes.length} selecionado${typeObjectTypes.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 -mt-1">Em que tipo de objeto este tipo de ocorrencia pode ser reportado.</p>

                    <button
                      type="button"
                      onClick={() => setTypeAvailableForAll(v => !v)}
                      data-testid="type-available-for-all-toggle"
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-left transition-colors ${typeAvailableForAll ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'}`}
                    >
                      <span
                        className={`w-4 h-4 rounded-sm flex-shrink-0 border ${typeAvailableForAll ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'} flex items-center justify-center`}
                      >
                        {typeAvailableForAll && <span className="text-white text-[10px] leading-none">✓</span>}
                      </span>
                      <Globe size={14} className={typeAvailableForAll ? 'text-emerald-600' : 'text-slate-400'} />
                      <span className="text-xs font-medium">Disponivel para todos os tipos de objeto</span>
                    </button>

                    <div
                      className={`grid grid-cols-2 gap-1.5 p-2 rounded-md border max-h-40 overflow-y-auto transition-opacity ${typeAvailableForAll ? 'bg-slate-100 border-slate-200 opacity-50 pointer-events-none' : 'bg-slate-50 border-slate-200'}`}
                      data-testid="type-object-types"
                      aria-disabled={typeAvailableForAll}
                    >
                      {objectTypesLoading && (
                        <p className="col-span-2 text-[11px] text-slate-500 text-center py-2">A carregar tipos de objeto...</p>
                      )}
                      {!objectTypesLoading && objectTypes.map(ot => {
                        const checked = !typeAvailableForAll && typeObjectTypes.some(id => String(id) === String(ot.id));
                        return (
                          <button
                            type="button"
                            key={ot.id}
                            disabled={typeAvailableForAll}
                            onClick={() => setTypeObjectTypes(prev => prev.some(id => String(id) === String(ot.id)) ? prev.filter(id => String(id) !== String(ot.id)) : [...prev, ot.id])}
                            data-testid={`type-object-type-${ot.id}`}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors border ${checked ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'} ${typeAvailableForAll ? 'cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`w-3.5 h-3.5 rounded-sm flex-shrink-0 border ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'} flex items-center justify-center`}
                            >
                              {checked && <span className="text-white text-[9px] leading-none">✓</span>}
                            </span>
                            <span className="text-xs truncate">{ot.name}</span>
                          </button>
                        );
                      })}
                      {!objectTypesLoading && objectTypes.length === 0 && (
                        <p className="col-span-2 text-[11px] text-slate-500 text-center py-2">Sem tipos de objeto disponiveis.</p>
                      )}
                    </div>
                    {(() => {
                      if (typeAvailableForAll) {
                        return (
                          <div className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded border bg-emerald-50 border-emerald-300 text-emerald-900" data-testid="type-eligible-count">
                            <Globe size={14} />
                            <span>
                              <strong>Todos</strong> os objetos da frota podem reportar este tipo de ocorrencia.
                            </span>
                          </div>
                        );
                      }
                      const count = countSelectedObjectTypes(typeObjectTypes, objectTypes);
                      if (typeObjectTypes.length === 0) {
                        return <p className="text-[11px] text-slate-400 italic">Selecione tipos de objeto (ou marque "Disponivel para todos") para ver quantos elegiveis.</p>;
                      }
                      const tone = count === 0 ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-emerald-50 border-emerald-300 text-emerald-900';
                      return (
                        <div className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded border ${tone}`} data-testid="type-eligible-count">
                          <span className="text-base leading-none">{count === 0 ? '!' : '✓'}</span>
                          <span>
                            <strong>{count}</strong> objeto{count === 1 ? '' : 's'} da frota {count === 1 ? 'pode' : 'podem'} reportar este tipo de ocorrencia.
                            {count === 0 && <em className="block text-[10px] mt-0.5 opacity-80">Sem objetos compativeis - este tipo nao vai aparecer em nenhum QR code.</em>}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Modo de contacto telefónico (pedido a) */}
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Phone size={14} className="text-blue-600" />
                      Número do alertante
                    </Label>
                    <Select value={phoneMode} onValueChange={setPhoneMode}>
                      <SelectTrigger data-testid="phone-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Qualquer número (sem validação)</SelectItem>
                        <SelectItem value="confirm">Confirmar número (SMS OTP)</SelectItem>
                        <SelectItem value="registered_only">Apenas números já registados</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">
                      {phoneMode === 'any' && 'Aceitar qualquer número sem validação — o mais permissivo.'}
                      {phoneMode === 'confirm' && 'O alertante recebe um SMS com código OTP para confirmar o número.'}
                      {phoneMode === 'registered_only' && 'Rejeita ocorrências de números não registados no sistema. Útil para frota interna.'}
                    </p>
                  </div>

                  {/* Alertantes autorizados (não fazem login no sistema) */}
                  {authorizedReportersLoading && (
                    <Card className="p-3 border-slate-200 bg-slate-50 text-xs text-slate-500">
                      A carregar contactos...
                    </Card>
                  )}
                  <AuthorizedReportersPicker
                    pool={authorizedReporters}
                    onPoolChange={setAuthorizedReporters}
                    selectedIds={typeAuthorizedReporterIds}
                    onSelectedIdsChange={setTypeAuthorizedReporterIds}
                    contactTypes={externalContactTypes}
                    contactTypesLoading={externalContactTypesLoading}
                    restrictedMode={phoneMode === 'registered_only'}
                    testIdPrefix="reporters"
                  />

                  {/* Estado Ativo/Inativo */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <Label className="text-sm font-medium">Estado</Label>
                      <p className="text-xs text-slate-600">Tipo ativo pode ser usado para novas ocorrências</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <Switch checked={isActive} onCheckedChange={setIsActive} />
                    </div>
                  </div>
                </div>

                {/* Formas de Contacto */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                    <Phone size={18} />
                    Formas de Contacto
                  </h3>
                  <div className="space-y-3">
                    {/* EMAIL */}
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <Mail className="text-blue-600" size={20} />
                          <div>
                            <Label className="text-sm font-medium">Email</Label>
                            <p className="text-xs text-slate-600">Enviar notificação por email</p>
                          </div>
                        </div>
                        <Switch checked={contactEmail} onCheckedChange={setContactEmail} />
                      </div>
                      {contactEmail && (
                        <div className="border-t border-slate-200 bg-blue-50/40 p-3 space-y-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Configurar email</p>
                          <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Assunto" data-testid="email-subject" />
                          <Textarea rows={4} value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Corpo do email" data-testid="email-body" />
                          <div className="flex flex-wrap gap-1">
                            {['{{$nome}}', '{{$matricula}}', '{{$tipo}}', '{{$localizacao}}', '{{$alertante}}', '{{$link}}'].map(v => (
                              <Badge key={v} variant="outline" className="text-[10px] font-mono cursor-pointer hover:bg-blue-100"
                                     onClick={() => setEmailBody(b => b + ' ' + v)}>{v}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* SMS */}
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <MessageSquare className="text-emerald-600" size={20} />
                          <div>
                            <Label className="text-sm font-medium">SMS</Label>
                            <p className="text-xs text-slate-600">Enviar notificação por SMS</p>
                          </div>
                        </div>
                        <Switch checked={contactSMS} onCheckedChange={setContactSMS} />
                      </div>
                      {contactSMS && (
                        <div className="border-t border-slate-200 bg-emerald-50/40 p-3 space-y-3">
                          <div className="flex items-start gap-2 p-2 bg-white rounded border border-emerald-200">
                            <Info size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                            <div className="text-[11px] text-slate-600">
                              <strong className="text-slate-800">SMS único de envio.</strong>
                              <span className="ml-1">O sistema envia <strong>apenas esta SMS</strong> ao alertante quando a ocorrência é reportada.</span>
                            </div>
                          </div>
                          <div className="bg-white rounded border-2 border-blue-300 p-3 space-y-2" data-testid="sms-single-block">
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-600">📨 Mensagem SMS</Label>
                            <Textarea
                              rows={3}
                              value={smsNotificationText}
                              onChange={e => setSmsNotificationText(e.target.value)}
                              placeholder="Texto da SMS a enviar ao alertante"
                              data-testid="sms-notification-input"
                            />
                            <div className="flex items-center justify-between text-[10px]">
                              <div className="flex flex-wrap gap-1">
                                {['{{$nome}}', '{{$matricula}}', '{{$tipo}}', '{{$alertante}}', '{{$atributo}}', '{{$link}}'].map(v => (
                                  <Badge
                                    key={v}
                                    variant="outline"
                                    className="text-[10px] font-mono cursor-pointer hover:bg-emerald-100"
                                    onClick={() => setSmsNotificationText(t => t + ' ' + v)}
                                  >{v}</Badge>
                                ))}
                              </div>
                              <span className="text-slate-500 font-mono">{smsNotificationText.length} chars</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CALL */}
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <Phone className="text-red-600" size={20} />
                          <div>
                            <Label className="text-sm font-medium">Chamada Telefónica</Label>
                            <p className="text-xs text-slate-600">Chamada com IVR (teclas para ações)</p>
                          </div>
                        </div>
                        <Switch checked={contactCall} onCheckedChange={setContactCall} />
                      </div>
                      {contactCall && (
                        <div className="border-t border-slate-200 bg-red-50/40 p-3 space-y-3">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mensagem da chamada (TTS)</p>
                          <Textarea rows={3} value={callQuestion} onChange={e => setCallQuestion(e.target.value)}
                                    placeholder="Mensagem de voz que será lida ao destinatário" data-testid="call-question" />
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 pt-1">Opções de teclado</p>
                          <div className="space-y-2">
                            {callOptions.map((opt, idx) => {
                              return (
                                <div key={opt.key + '-' + idx} className="bg-white rounded border border-red-200 overflow-hidden">
                                  <div className="flex items-center gap-2 p-2">
                                    <div className="w-9 h-9 rounded bg-red-600 text-white font-bold flex items-center justify-center text-base flex-shrink-0">
                                      {opt.key}
                                    </div>
                                    <Input className="h-8 text-xs flex-1" value={opt.label}
                                           onChange={e => setCallOptions(prev => prev.map((o, i) => i === idx ? { ...o, label: e.target.value } : o))}
                                           placeholder="Texto a anunciar (ex: Confirmar...)" />
                                    <Select value={opt.action}
                                            onValueChange={v => setCallOptions(prev => prev.map((o, i) => i === idx ? { ...o, action: v } : o))}>
                                      <SelectTrigger className="h-8 w-[200px] text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {callOptions.map(callOption => (
                                          <SelectItem key={`${callOption.key}-${callOption.action}`} value={callOption.action}>
                                            {callOption.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                                            onClick={() => setCallOptions(prev => prev.filter((_, i) => i !== idx))}>
                                      <Trash2 size={12} />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <Button variant="outline" size="sm" className="w-full"
                                  onClick={() => {
                                    const used = new Set(callOptions.map(o => o.key));
                                    const free = ['1','2','3','4','5','6','7','8','9','0','*','#'].find(k => !used.has(k)) || '#';
                                    setCallOptions(prev => [...prev, { key: free, label: '', action: 'callback' }]);
                                  }}
                                  data-testid="add-call-option">
                            <Plus size={12} className="mr-1" /> Adicionar tecla
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Validação de Número */}
                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                    <User size={18} />
                    Validação de Número do Alertante
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                      <div>
                        <Label className="text-sm font-medium">Obrigar Inserir Número</Label>
                        <p className="text-xs text-slate-600">Alertante deve fornecer número de telefone</p>
                      </div>
                      <Switch checked={requirePhone} onCheckedChange={setRequirePhone} />
                    </div>

                    {requirePhone && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-1">Reconhecimento Automático</p>
                        <p className="text-xs text-blue-700">
                          O sistema associa automaticamente o número ao nome do alertante. Próximas ocorrências do mesmo número mostrarão o histórico da pessoa.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Geração de Ticket */}
                <div className="space-y-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h3 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                    <TicketIcon size={18} />
                    Geração de Ticket
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
                      <div>
                        <Label className="text-sm font-medium">Gerar Ticket Automaticamente</Label>
                        <p className="text-xs text-slate-600">Criar ticket quando ocorrência for reportada</p>
                      </div>
                      <Switch checked={generateTicket} onCheckedChange={setGenerateTicket} />
                    </div>

                    {generateTicket && (
                      <>
                        <div className="space-y-2">
                          <Label>Quem Será Notificado *</Label>
                          <p className="text-[11px] text-slate-500 -mt-1">As notificações de tickets podem ser enviadas por <strong>Email</strong> e/ou <strong>SMS</strong>.</p>
                          <div className="p-3 bg-white rounded-lg border border-emerald-200 divide-y divide-slate-100">
                            {ticketNotifyUsersLoading && (
                              <p className="text-center text-xs text-slate-500 py-3">A carregar utilizadores...</p>
                            )}
                            {!ticketNotifyUsersLoading && ticketNotifyUsers.length === 0 && (
                              <p className="text-center text-xs text-slate-400 italic py-3">Sem utilizadores disponiveis para notificar.</p>
                            )}
                            {ticketNotifyUsers.map((u) => (
                              <div key={u.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0" data-testid={`ticket-notify-row-${u.id}`}>
                                <Checkbox
                                  id={`notify-${u.id}`}
                                  checked={u.enabled}
                                  onCheckedChange={(v) =>
                                    setTicketNotifyUsers(prev => prev.map(x => String(x.id) === String(u.id) ? { ...x, enabled: !!v } : x))
                                  }
                                  disabled={ticketNotifyUsersLoading}
                                  data-testid={`ticket-notify-enabled-${u.id}`}
                                />
                                <label htmlFor={`notify-${u.id}`} className="text-sm flex-1 cursor-pointer">{u.name}</label>
                                <div className={`flex items-center gap-3 transition-opacity ${u.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                                  <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
                                    <Checkbox
                                      checked={u.email}
                                      onCheckedChange={(v) =>
                                        setTicketNotifyUsers(prev => prev.map(x => String(x.id) === String(u.id) ? { ...x, email: !!v } : x))
                                      }
                                      disabled={!u.enabled || ticketNotifyUsersLoading}
                                      data-testid={`ticket-notify-email-${u.id}`}
                                    />
                                    <Mail size={13} className="text-blue-600" />
                                    <span>Email</span>
                                  </label>
                                  <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
                                    <Checkbox
                                      checked={u.sms}
                                      onCheckedChange={(v) =>
                                        setTicketNotifyUsers(prev => prev.map(x => String(x.id) === String(u.id) ? { ...x, sms: !!v } : x))
                                      }
                                      disabled={!u.enabled || ticketNotifyUsersLoading}
                                      data-testid={`ticket-notify-sms-${u.id}`}
                                    />
                                    <MessageSquare size={13} className="text-emerald-600" />
                                    <span>SMS</span>
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {(() => {
                          const enabled = ticketNotifyUsers.filter(u => u.enabled);
                          const invalid = enabled.filter(u => !u.email && !u.sms);
                          if (invalid.length > 0) {
                            return (
                              <div className="p-3 bg-amber-50 rounded-lg border border-amber-300">
                                <p className="text-xs font-medium text-amber-900">⚠ Configuração incompleta</p>
                                <p className="text-xs text-amber-700 mt-1">
                                  {invalid.map(u => u.name).join(', ')} sem canal selecionado. Marque pelo menos Email ou SMS.
                                </p>
                              </div>
                            );
                          }
                          if (enabled.length === 0) {
                            return (
                              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <p className="text-xs text-slate-600">Nenhum utilizador será notificado quando ticket for criado.</p>
                              </div>
                            );
                          }
                          return (
                            <div className="p-3 bg-emerald-100 rounded-lg border border-emerald-300">
                              <p className="text-xs font-medium text-emerald-900">✓ Notificações Configuradas</p>
                              <p className="text-xs text-emerald-700 mt-1">
                                {enabled.length} utilizador{enabled.length === 1 ? '' : 'es'} receberá{enabled.length === 1 ? '' : 'ão'} notificação por{' '}
                                {enabled.map(u => {
                                  const ch = [u.email && 'Email', u.sms && 'SMS'].filter(Boolean).join('+');
                                  return `${u.name.split(' (')[0]} (${ch})`;
                                }).join(', ')} quando ticket for criado.
                              </p>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {/* Anexos Permitidos */}
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="font-secondary font-bold text-slate-900 flex items-center gap-2">
                    <Camera size={18} />
                    Anexos Permitidos
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Camera className="text-purple-600" size={20} />
                        <div>
                          <Label className="text-sm font-medium">Fotos</Label>
                          <p className="text-xs text-slate-600">Permitir upload de fotos</p>
                        </div>
                      </div>
                      <Switch checked={allowPhotos} onCheckedChange={setAllowPhotos} />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Video className="text-purple-600" size={20} />
                        <div>
                          <Label className="text-sm font-medium">Vídeos</Label>
                          <p className="text-xs text-slate-600">Permitir upload de vídeos</p>
                        </div>
                      </div>
                      <Switch checked={allowVideos} onCheckedChange={setAllowVideos} />
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <FileText className="text-purple-600" size={20} />
                        <div>
                          <Label className="text-sm font-medium">Descrição de Texto</Label>
                          <p className="text-xs text-slate-600">Permitir campo de texto livre</p>
                        </div>
                      </div>
                      <Switch checked={allowText} onCheckedChange={setAllowText} />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { resetForm(); setIsDialogOpen(false); }}>Cancelar</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSave}
                  disabled={submitting || prioritiesLoading || ticketNotifyUsersLoading || objectTypesLoading || !typeName || !typeCategory || !typePriority || !typeIcon || (!typeAvailableForAll && typeObjectTypes.length === 0) || (isEditMode ? !canUpdateIncidentTypes : !canCreateIncidentTypes)}
                >
                  {isEditMode ? 'Guardar Alterações' : 'Criar Tipo'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Tipos</p>
              <AlertCircle className="text-blue-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">{incidentTypes.length}</p>
            <p className="text-xs text-slate-500 mt-1">{incidentTypes.filter(t => t.active).length} ativos</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Geram Tickets</p>
              <TicketIcon className="text-emerald-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">
              {incidentTypes.filter(t => t.generateTicket).length}
            </p>
            <p className="text-xs text-slate-500 mt-1">Criação automática</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Com Piket</p>
              <Phone className="text-red-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">
              {incidentTypes.filter(t => t.contactMethods.includes('call')).length}
            </p>
            <p className="text-xs text-slate-500 mt-1">Chamada automática</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Reports</p>
              <AlertCircle className="text-purple-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">
              {incidentTypes.reduce((acc, t) => acc + t.totalReports, 0)}
            </p>
            <p className="text-xs text-slate-500 mt-1">Todos os tipos</p>
          </Card>
        </div>

        {typesError && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {typesError}
          </Card>
        )}

        {departmentsError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {departmentsError}
          </Card>
        )}

        {prioritiesError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {prioritiesError}
          </Card>
        )}

        {iconsError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {iconsError}
          </Card>
        )}

        {objectTypesError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {objectTypesError}
          </Card>
        )}

        {authorizedReportersError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {authorizedReportersError}
          </Card>
        )}

        {externalContactTypesError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {externalContactTypesError}
          </Card>
        )}

        {ticketNotifyUsersError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {ticketNotifyUsersError}
          </Card>
        )}

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Ordem</TableHead>
                <TableHead>Nome / Descrição</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Objetos</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Validação</TableHead>
                <TableHead>Ticket</TableHead>
                <TableHead>Anexos</TableHead>
                <TableHead>Reports</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingTypes && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-sm text-slate-500 py-8">
                    A carregar tipos de ocorrencia...
                  </TableCell>
                </TableRow>
              )}
              {!loadingTypes && incidentTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-sm text-slate-500 py-8">
                    Sem tipos de ocorrencia para mostrar.
                  </TableCell>
                </TableRow>
              )}
              {!loadingTypes && incidentTypes.map((type, idx) => (
                <TableRow key={type.id} className="hover:bg-slate-50" data-testid={`type-row-${type.id}`}>
                  <TableCell>
                    <div className="flex flex-col gap-0.5 items-center">
                      <button type="button" disabled={idx === 0}
                        onClick={() => moveType(idx, -1)}
                        data-testid={`move-up-${type.id}`}
                        className="h-5 w-5 rounded text-slate-500 hover:bg-slate-100 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        title="Mover para cima">
                        <ArrowUp size={12} />
                      </button>
                      <span className="text-[10px] text-slate-400 font-mono">{idx + 1}</span>
                      <button type="button" disabled={idx === incidentTypes.length - 1}
                        onClick={() => moveType(idx, 1)}
                        data-testid={`move-down-${type.id}`}
                        className="h-5 w-5 rounded text-slate-500 hover:bg-slate-100 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        title="Mover para baixo">
                        <ArrowDown size={12} />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{type.name}</p>
                      <p className="text-xs text-slate-600">{type.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{type.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(type.objectTypeIds || []).map(id => {
                          const ot = findObjectTypeById(objectTypes, id);
                          if (!ot) return null;
                          return (
                            <Badge
                              key={id}
                              variant="outline"
                              className="text-[10px] px-1.5 py-0"
                              style={{ borderColor: ot.color, color: ot.color }}
                            >
                              {ot.name}
                            </Badge>
                          );
                        })}
                        {(!type.objectTypeIds || type.objectTypeIds.length === 0) && (
                          <span className="text-[10px] text-slate-400 italic">Todos</span>
                        )}
                      </div>
                      {(() => {
                        const count = countSelectedObjectTypes(type.objectTypeIds, objectTypes);
                        const tone = count === 0 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-blue-700 bg-blue-50 border-blue-200';
                        return (
                          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${tone}`}
                                title="Tipos de objeto que podem reportar este tipo">
                            {count === 0 ? '⚠ ' : '👁 '}
                            <strong>{count}</strong> tipo{count === 1 ? '' : 's'}
                          </span>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getContactMethodsDisplay(type.contactMethods)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {type.requirePhone && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">
                          📱 Número
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {type.generateTicket ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        ✓ Sim
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">
                        - Não
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {type.allowPhotos && <Camera size={14} className="text-purple-600" />}
                      {type.allowVideos && <Video size={14} className="text-purple-600" />}
                      {type.allowText && <FileText size={14} className="text-purple-600" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-bold">
                      {type.totalReports}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${canUpdateIncidentTypes ? 'cursor-pointer' : 'cursor-default'} ${submitting ? 'opacity-60 pointer-events-none' : ''} ${type.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      onClick={canUpdateIncidentTypes ? () => handleToggleActive(type.id) : undefined}
                    >
                      {type.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {canUpdateIncidentTypes && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-type-${type.id}`}
                          onClick={() => handleOpenEdit(type)}
                          disabled={submitting}
                        >
                          <Edit size={14} />
                        </Button>
                      )}
                      {canDeleteIncidentTypes && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-type-${type.id}`}
                          onClick={() => handleDelete(type.id)}
                          disabled={submitting}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* ================== Quick Create (mini-form) ================== */}
        <Dialog open={isQuickCreateOpen} onOpenChange={setIsQuickCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="text-blue-600" size={20} />
                Novo Tipo de Ocorrência
              </DialogTitle>
              <DialogDescription>
                Comece com o essencial. Pode configurar canais (Email/SMS/Chamada), validações, anexos e geração de ticket depois, ao editar o tipo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm">Nome *</Label>
                <Input value={qcName} onChange={e => setQcName(e.target.value)}
                       placeholder="Ex: Cabo danificado"
                       data-testid="qc-name-input" />
                <p className="text-[11px] text-slate-500 -mt-0.5">
                  <Info size={11} className="inline mr-1 -mt-0.5" />
                  Os tipos serão apresentados pelo <strong>nome</strong> e <strong>ícone</strong> na vista do alertante.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Descrição</Label>
                <Textarea rows={2} value={qcDescription} onChange={e => setQcDescription(e.target.value)}
                          placeholder="Breve descrição do tipo de ocorrência" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Categoria</Label>
                <Select value={qcCategory} onValueChange={setQcCategory}>
                  <SelectTrigger data-testid="qc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas" key={0}>Todas as categorias</SelectItem>
                    {departments.map(department => (
                      <SelectItem key={department.id} value={departmentCategoryKey(department.id)}>
                        {department.name}
                      </SelectItem>
                    ))}
                    {/*<SelectItem value="sem">Sem categoria</SelectItem>*/}
                  </SelectContent>
                </Select>
              </div>
              {/* Ícone */}
              <IconPicker value={qcIcon} onChange={setQcIcon} icons={icons} loading={iconsLoading} testIdPrefix="qc-icon" />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Tipos de Objeto aplicáveis *</Label>
                  <span className="text-[10px] text-slate-500">
                    {qcAvailableForAll ? 'todos' : `${qcObjectTypes.length} selecionado${qcObjectTypes.length === 1 ? '' : 's'}`}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 -mt-1">Em que tipo de objeto este tipo de ocorrência pode ser reportado.</p>

                {/* Toggle: Disponível para todos */}
                <button
                  type="button"
                  onClick={() => setQcAvailableForAll(v => !v)}
                  data-testid="qc-available-for-all-toggle"
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-left transition-colors ${qcAvailableForAll ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'}`}
                >
                  <span
                    className={`w-4 h-4 rounded-sm flex-shrink-0 border ${qcAvailableForAll ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white'} flex items-center justify-center`}
                  >
                    {qcAvailableForAll && <span className="text-white text-[10px] leading-none">✓</span>}
                  </span>
                  <Globe size={14} className={qcAvailableForAll ? 'text-emerald-600' : 'text-slate-400'} />
                  <span className="text-xs font-medium">Disponível para todos os tipos de objeto</span>
                </button>

                <div
                  className={`grid grid-cols-2 gap-1.5 p-2 rounded-md border max-h-40 overflow-y-auto transition-opacity ${qcAvailableForAll ? 'bg-slate-100 border-slate-200 opacity-50 pointer-events-none' : 'bg-slate-50 border-slate-200'}`}
                  data-testid="qc-object-types"
                  aria-disabled={qcAvailableForAll}
                >
                  {objectTypesLoading && (
                    <p className="col-span-2 text-[11px] text-slate-500 text-center py-2">A carregar tipos de objeto...</p>
                  )}
                  {!objectTypesLoading && objectTypes.map(ot => {
                    const checked = !qcAvailableForAll && qcObjectTypes.some(id => String(id) === String(ot.id));
                    return (
                      <button
                        type="button"
                        key={ot.id}
                        disabled={qcAvailableForAll}
                        onClick={() => setQcObjectTypes(prev => prev.some(id => String(id) === String(ot.id)) ? prev.filter(id => String(id) !== String(ot.id)) : [...prev, ot.id])}
                        data-testid={`qc-object-type-${ot.id}`}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors border ${checked ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'} ${qcAvailableForAll ? 'cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded-sm flex-shrink-0 border ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'} flex items-center justify-center`}
                        >
                          {checked && <span className="text-white text-[9px] leading-none">✓</span>}
                        </span>
                        <span className="text-xs truncate">{ot.name}</span>
                      </button>
                    );
                  })}
                  {!objectTypesLoading && objectTypes.length === 0 && (
                    <p className="col-span-2 text-[11px] text-slate-500 text-center py-2">Sem tipos de objeto disponiveis.</p>
                  )}
                </div>
                {(() => {
                  if (qcAvailableForAll) {
                    return (
                      <div className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded border bg-emerald-50 border-emerald-300 text-emerald-900" data-testid="qc-eligible-count">
                        <Globe size={14} />
                        <span>
                          <strong>Todos</strong> os objetos da frota podem reportar este tipo de ocorrência.
                        </span>
                      </div>
                    );
                  }
                  const count = countSelectedObjectTypes(qcObjectTypes, objectTypes);
                  if (qcObjectTypes.length === 0) {
                    return <p className="text-[11px] text-slate-400 italic">Selecione tipos de objeto (ou marque "Disponível para todos") para ver quantos elegíveis.</p>;
                  }
                  const tone = count === 0 ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-emerald-50 border-emerald-300 text-emerald-900';
                  return (
                    <div className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded border ${tone}`} data-testid="qc-eligible-count">
                      <span className="text-base leading-none">{count === 0 ? '⚠' : '✓'}</span>
                      <span>
                        <strong>{count}</strong> objeto{count === 1 ? '' : 's'} da frota {count === 1 ? 'pode' : 'podem'} reportar este tipo de ocorrência.
                        {count === 0 && <em className="block text-[10px] mt-0.5 opacity-80">Sem objetos compatíveis — este tipo não vai aparecer em nenhum QR code.</em>}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-2">
                <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-900">
                  <strong>Defaults aplicados:</strong> Email ON · SMS OFF · Chamada OFF · Geração de ticket ON · Anexos foto e texto ON · Estado Ativo. Pode ajustar tudo depois clicando em <strong>Editar</strong>.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsQuickCreateOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button variant="outline" onClick={() => handleQuickCreate({ thenEdit: false })} data-testid="qc-create-btn" disabled={submitting || !canCreateIncidentTypes || !qcIcon}>
                Criar e fechar
              </Button>
              {canUpdateIncidentTypes && (
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleQuickCreate({ thenEdit: true })}
                        data-testid="qc-create-and-edit-btn" disabled={submitting || !canCreateIncidentTypes || !qcIcon}>
                  Criar e configurar canais
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ================== Mobile Preview Dialog ================== */}
        <Dialog open={previewMobileOpen} onOpenChange={setPreviewMobileOpen}>
          <DialogContent className="max-w-[420px] p-0 overflow-hidden bg-transparent border-0 shadow-none" data-testid="preview-mobile-dialog">
            <DialogHeader className="sr-only">
              <DialogTitle>Pré-visualização mobile</DialogTitle>
              <DialogDescription>Vista mobile do alertante no condutor.online</DialogDescription>
            </DialogHeader>
            <div className="bg-slate-900 rounded-[40px] p-3 shadow-2xl">
              {/* Phone notch */}
              <div className="bg-slate-900 h-6 flex items-center justify-center mb-1">
                <div className="w-24 h-5 bg-black rounded-full" />
              </div>
              {/* Phone screen */}
              <div className="bg-white rounded-[28px] overflow-hidden h-[640px] flex flex-col">
                {/* status bar */}
                <div className="flex items-center justify-between px-5 py-2 text-[10px] font-bold text-slate-700 bg-slate-50">
                  <span>9:41</span>
                  <div className="flex items-center gap-1"><span>●●●●</span><span>📶</span><span>🔋</span></div>
                </div>
                {/* Yellow header (condutor.online style — sem personalização de branding) */}
                <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#facc15' }}>
                  <div className="text-[11px] font-bold text-slate-900 leading-tight">
                    <p>condutor.online</p>
                    <p className="text-[9px] text-slate-700 font-normal">Reportar ocorrência</p>
                  </div>
                  <Link
                    to="/login?force=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Login universal"
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-white hover:bg-slate-100 transition-colors text-slate-700 text-[10px] font-semibold"
                    data-testid="preview-login-link"
                  >
                    <LogIn size={12} />
                    <span>Login</span>
                  </Link>
                </div>
                {/* Tag info */}
                <div className="px-4 py-3 bg-blue-700 text-white">
                  <p className="text-[10px] uppercase opacity-80 tracking-wider">Tag identificada</p>
                  <p className="font-mono font-bold text-base">TAG-000000</p>
                  <p className="text-[10px] opacity-80">Objeto identificado</p>
                </div>
                {/* List of types (em ordem) */}
                <div className="flex-1 overflow-y-auto bg-slate-50 flex flex-col">
                  <div className="flex items-center justify-between px-3 pt-3 pb-1">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold px-1">Selecione o tipo</p>
                    <div className="flex items-center gap-1 bg-white rounded-full p-0.5 border border-slate-200">
                      <button
                        type="button"
                        onClick={() => setPreviewMobileLayout('list')}
                        data-testid="preview-layout-list"
                        className={`p-1 rounded-full transition-colors ${previewMobileLayout === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-700'}`}
                        title="Lista"
                      >
                        <ListIcon size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMobileLayout('grid')}
                        data-testid="preview-layout-grid"
                        className={`p-1 rounded-full transition-colors ${previewMobileLayout === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-700'}`}
                        title="Grelha"
                      >
                        <LayoutGrid size={11} />
                      </button>
                    </div>
                  </div>

                  {previewMobileLayout === 'list' ? (
                    <div className="flex-1 p-3 pt-1 space-y-2">
                      {incidentTypes.filter(t => t.active).map((type, i) => {
                        const selectedIcon = icons.find(icon => String(icon.id) === String(type.icon));
                        return (
                          <button key={type.id} type="button"
                                  className="w-full bg-white border-2 border-slate-200 hover:border-blue-500 rounded-xl p-3 flex items-center gap-3 transition-all">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-base font-bold flex-shrink-0"
                                 style={{ backgroundColor: '#facc15', color: '#0c1a3a' }}>
                              {selectedIcon ? <ApiIcon icon={selectedIcon} size={18} /> : (i + 1)}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-bold text-sm text-slate-900 leading-tight truncate">{type.name}</p>
                              <p className="text-[10px] text-slate-500 truncate">{type.description}</p>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {(type.objectTypeIds || []).slice(0, 2).map(id => {
                                  const ot = findObjectTypeById(objectTypes, id);
                                  if (!ot) return null;
                                  return (
                                    <Badge
                                      key={id}
                                      variant="outline"
                                      className="text-[8px] px-1 py-0"
                                      style={{ borderColor: ot.color, color: ot.color }}
                                    >
                                      {ot.name}
                                    </Badge>
                                  );
                                })}
                                {type.requirePhone && <Badge variant="outline" className="text-[8px] px-1 py-0">📱</Badge>}
                                {type.generateTicket && <Badge variant="outline" className="text-[8px] px-1 py-0">🎫</Badge>}
                              </div>
                            </div>
                            <ChevronRight size={16} className="text-slate-400" />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    // GRID layout — 2 colunas, tiles amarelos grandes com ícone (se houver) + nome
                    <div className="flex-1 p-3 pt-1 grid grid-cols-2 gap-2 auto-rows-[130px]">
                      {incidentTypes.filter(t => t.active).map((type) => {
                        const selectedIcon = icons.find(icon => String(icon.id) === String(type.icon));
                        return (
                          <button key={type.id} type="button"
                                  className="rounded-xl p-2 flex flex-col items-center justify-center gap-1.5 text-center transition-all hover:brightness-95 shadow-sm"
                                  style={{ backgroundColor: '#facc15', color: '#0c1a3a' }}>
                            {selectedIcon ? (
                              <ApiIcon icon={selectedIcon} size={34} />
                            ) : (
                              <div className="h-[34px]" />
                            )}
                            <p className="font-bold text-[11px] leading-tight">{type.name}</p>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {incidentTypes.filter(t => t.active).length === 0 && (
                    <p className="text-center text-xs text-slate-400 py-8">Sem tipos ativos para mostrar.</p>
                  )}
                </div>
                {/* Footer */}
                <div className="bg-slate-100 border-t border-slate-200 px-4 py-2 text-center">
                  <p className="text-[9px] text-slate-500">Powered by condutor.online</p>
                </div>
              </div>
              {/* home indicator */}
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-32 h-1 bg-slate-700 rounded-full" />
              </div>
            </div>
            <p className="text-center text-xs text-slate-500 mt-3">Pré-visualização da vista mobile do alertante (condutor.online)</p>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
